import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import JsSIP from "jssip";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { getSavedSipCredentials } from "../components/SipSetupDialog";

import { API_URL } from '../utils/api';
import notificationService from '../services/notificationService';
const API = API_URL;

const SipContext = createContext(null);

export function useSip() {
  const context = useContext(SipContext);
  if (!context) {
    throw new Error("useSip must be used within a SipProvider");
  }
  return context;
}

// ==================== PROVIDER AUTO-DETECTION ====================
// Maps SIP domains to their WebSocket URLs for provider-agnostic operation
const SIP_PROVIDERS = {
  'securevoip.nz': {
    wsServer: 'wss://wss.securevoip.nz:7443',
    fallbackWsServers: ['wss://wss.hero.co.nz:7443'],
    sipDomain: 'securevoip.nz',
    name: 'Hero Wholesale'
  },
  'hero.co.nz': {
    wsServer: 'wss://wss.hero.co.nz:7443',
    fallbackWsServers: ['wss://wss.securevoip.nz:7443'],
    sipDomain: 'hero.co.nz',
    name: 'Hero Retail'
  },
  '2talk.co.nz': {
    wsServer: 'wss://tls.2talk.co.nz:7443',
    sipDomain: '2talk.co.nz',
    name: '2talk'
  }
};

/**
 * Auto-detect SIP provider configuration from the user's sip_domain
 * The API returns either a full WSS URL or just the domain name
 */
function detectSipProvider(sipDomainFromApi) {
  if (!sipDomainFromApi) return null;
  
  // If it's already a full WSS URL (e.g., wss://wss.securevoip.nz:7443)
  if (sipDomainFromApi.startsWith('wss://')) {
    try {
      const url = new URL(sipDomainFromApi);
      const hostname = url.hostname.replace(/^wss\./, '');
      
      // Find matching provider
      for (const [domain, config] of Object.entries(SIP_PROVIDERS)) {
        if (hostname.includes(domain) || sipDomainFromApi.includes(domain)) {
          return {
            ...config,
            wsServer: sipDomainFromApi // Use the exact URL from API
          };
        }
      }
      
      // Unknown provider - use the URL as-is
      return {
        wsServer: sipDomainFromApi,
        sipDomain: hostname,
        name: 'Custom'
      };
    } catch {
      // Invalid URL, try as domain
    }
  }
  
  // It's a domain name (e.g., securevoip.nz)
  const normalizedDomain = sipDomainFromApi.toLowerCase()
    .replace(/^sip\./, '')
    .replace(/^wss\./, '');
  
  // Check known providers
  for (const [domain, config] of Object.entries(SIP_PROVIDERS)) {
    if (normalizedDomain.includes(domain)) {
      return config;
    }
  }
  
  // Unknown provider - construct default WSS URL
  return {
    wsServer: `wss://wss.${normalizedDomain}:7443`,
    sipDomain: normalizedDomain,
    name: 'Custom'
  };
}

export function SipProvider({ children }) {
  const { user, token, isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Check if user is in the app area (not on public pages)
  const isInAppArea = location.pathname.startsWith('/app');
  
  // SIP state
  const [sipStatus, setSipStatus] = useState("disconnected");
  const [callStatus, setCallStatus] = useState("idle");
  const [incomingCallInfo, setIncomingCallInfo] = useState(null);
  const [callerDetails, setCallerDetails] = useState(null);
  const [callerHistory, setCallerHistory] = useState([]);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [isOnHold, setIsOnHold] = useState(false);
  const [outputDeviceId, setOutputDeviceId] = useState(() => localStorage.getItem('preferredSpeaker') || '');
  const [sipProvider, setSipProvider] = useState(null);
  
  // State for "add contact" prompt after call ends with unknown caller
  const [endedCallInfo, setEndedCallInfo] = useState(null);
  
  // Refs
  const userAgentRef = useRef(null);
  const sessionRef = useRef(null);
  const callTimerRef = useRef(null);
  const audioRef = useRef(null);
  const sipStatusRef = useRef("disconnected");
  const remoteStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const ringtoneIntervalRef = useRef(null);
  const localStreamRef = useRef(null);
  const currentCallInfoRef = useRef(null); // Track current call info for "add contact" prompt
  const statusHeartbeatRef = useRef(null); // Periodic heartbeat for active call status
  
  // Keep outputDeviceId in sync with localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferredSpeaker' && e.newValue !== outputDeviceId) {
        console.log('Speaker preference changed:', e.newValue);
        setOutputDeviceId(e.newValue || '');
        
        if (audioRef.current && e.newValue && typeof audioRef.current.setSinkId === 'function') {
          audioRef.current.setSinkId(e.newValue).catch(err => {
            console.warn('Could not update audio output device:', err);
          });
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [outputDeviceId]);
  
  useEffect(() => {
    const handleFocus = () => {
      const storedSpeaker = localStorage.getItem('preferredSpeaker') || '';
      if (storedSpeaker !== outputDeviceId) {
        setOutputDeviceId(storedSpeaker);
        if (audioRef.current && storedSpeaker && typeof audioRef.current.setSinkId === 'function') {
          audioRef.current.setSinkId(storedSpeaker).catch(err => {
            console.warn('Could not update audio output device on focus:', err);
          });
        }
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [outputDeviceId]);

  // On mount, resolve server-stored speaker label to device ID if localStorage is empty
  useEffect(() => {
    const resolveServerSpeakerPref = async () => {
      const localSpeaker = localStorage.getItem('preferredSpeaker');
      if (localSpeaker) return; // Already have a local preference
      
      const serverLabel = user?.preferred_speaker_label;
      if (!serverLabel) return;
      
      try {
        // Need mic permission first to get real device labels
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(t => t.stop());
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        const match = devices.find(d => d.kind === 'audiooutput' && d.label === serverLabel);
        if (match) {
          localStorage.setItem('preferredSpeaker', match.deviceId);
          localStorage.setItem('preferredSpeakerLabel', serverLabel);
          setOutputDeviceId(match.deviceId);
          console.log('Resolved server speaker preference:', serverLabel, '->', match.deviceId);
          
          if (audioRef.current && typeof audioRef.current.setSinkId === 'function') {
            audioRef.current.setSinkId(match.deviceId).catch(() => {});
          }
        }
      } catch (e) {
        console.warn('Could not resolve server speaker preference:', e);
      }
      
      // Also resolve mic
      const localMic = localStorage.getItem('preferredMicrophone');
      if (!localMic && user?.preferred_mic_label) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const match = devices.find(d => d.kind === 'audioinput' && d.label === user.preferred_mic_label);
          if (match) {
            localStorage.setItem('preferredMicrophone', match.deviceId);
            localStorage.setItem('preferredMicrophoneLabel', user.preferred_mic_label);
            console.log('Resolved server mic preference:', user.preferred_mic_label, '->', match.deviceId);
          }
        } catch (e) {}
      }
    };
    
    if (user) resolveServerSpeakerPref();
  }, [user]);

  // Get SIP domain for URI construction
  const getSipDomain = useCallback(() => {
    if (sipProvider?.sipDomain) {
      return sipProvider.sipDomain;
    }
    if (!user?.sip_domain) return null;
    
    // Extract domain from WSS URL or use as-is
    try {
      const url = new URL(user.sip_domain);
      return url.hostname.replace(/^wss\./, '');
    } catch {
      return user.sip_domain.replace(/^wss?:\/\//, "").split(":")[0].split("/")[0];
    }
  }, [user?.sip_domain, sipProvider]);

  // Get ICE servers configuration
  const getIceServers = useCallback(() => {
    const servers = [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" }
    ];
    
    const turnServer = localStorage.getItem('turnServer');
    const turnUsername = localStorage.getItem('turnUsername');
    const turnPassword = localStorage.getItem('turnPassword');
    
    if (turnServer && turnUsername && turnPassword) {
      servers.push({
        urls: turnServer,
        username: turnUsername,
        credential: turnPassword
      });
      console.log("TURN server configured:", turnServer);
    }
    
    return servers;
  }, []);

  // Report status to backend
  const reportStatus = useCallback(async (status, remoteParty = null) => {
    if (!token || !user?.phone_number) return;
    try {
      await axios.post(`${API}/softphone/status`, {
        extension: user.phone_number,
        status,
        remote_party: remoteParty,
        call_id: sessionRef.current?.id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.warn("Failed to report status:", error);
    }
  }, [token, user?.phone_number]);

  // Fetch caller info and call history
  const fetchCallerInfo = useCallback(async (callerNumber) => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/contacts?search=${encodeURIComponent(callerNumber)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.length > 0) {
        const contact = response.data[0];
        setCallerDetails(contact);
        setIncomingCallInfo(prev => ({
          ...prev,
          name: contact.name || prev?.name
        }));
        // Mark as known contact for "add contact" prompt logic
        if (currentCallInfoRef.current) {
          currentCallInfoRef.current.isKnownContact = true;
          currentCallInfoRef.current.remoteName = contact.name;
        }
      } else {
        setCallerDetails({
          name: callerNumber,
          phone_number: callerNumber,
          isUnknown: true
        });
        // Mark as unknown contact
        if (currentCallInfoRef.current) {
          currentCallInfoRef.current.isKnownContact = false;
        }
      }
      
      try {
        const historyResponse = await axios.get(`${API}/cdr/records`, {
          params: { phone_number: callerNumber, limit: 10 },
          headers: { Authorization: `Bearer ${token}` }
        });
        setCallerHistory(historyResponse.data?.records || []);
      } catch (histError) {
        console.warn("Failed to fetch caller history:", histError);
        setCallerHistory([]);
      }
    } catch (error) {
      console.warn("Failed to fetch caller info:", error);
      setCallerDetails({
        name: callerNumber,
        phone_number: callerNumber,
        isUnknown: true
      });
      setCallerHistory([]);
      // Mark as unknown on error
      if (currentCallInfoRef.current) {
        currentCallInfoRef.current.isKnownContact = false;
      }
    }
  }, [token]);

  // ==================== RINGTONE FUNCTIONS ====================
  
  const stopRingtone = useCallback(() => {
    if (ringtoneIntervalRef.current) {
      clearInterval(ringtoneIntervalRef.current);
      ringtoneIntervalRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        if (audioContextRef.current instanceof AudioContext) {
          audioContextRef.current.close();
        } else if (audioContextRef.current.pause) {
          audioContextRef.current.pause();
          audioContextRef.current.currentTime = 0;
        }
      } catch (e) {
        // Ignore
      }
      audioContextRef.current = null;
    }
  }, []);

  const playRingtone = useCallback(() => {
    stopRingtone();
    
    // Load ringtone preference from user profile (synced) or localStorage (fallback)
    const toneId = user?.ringtone_preference || localStorage.getItem('preferredRingtone') || 'classic';
    
    // No ringtone = silent
    if (toneId === 'none') {
      console.log("Ringtone set to silent");
      return;
    }
    
    // Custom ringtone - play uploaded audio
    if (toneId === 'custom') {
      const customAudio = user?.custom_ringtone || localStorage.getItem('customRingtone');
      if (customAudio) {
        try {
          const audio = new Audio(customAudio);
          audio.loop = true;
          const preferredSpeaker = localStorage.getItem('preferredSpeaker');
          if (preferredSpeaker && typeof audio.setSinkId === 'function') {
            audio.setSinkId(preferredSpeaker).catch(() => {});
          }
          audio.play().catch(e => console.warn("Could not play custom ringtone:", e));
          // Store ref so stopRingtone can pause it
          audioContextRef.current = audio;
        } catch (e) {
          console.warn("Could not play custom ringtone:", e);
        }
      }
      return;
    }
    
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioContextRef.current;
      
      const tones = {
        classic:  { freq1: 440, freq2: 480, pattern: 'double' },
        modern:   { freq1: 523, freq2: 659, pattern: 'single' },
        gentle:   { freq1: 587, freq2: 784, pattern: 'triple' },
        urgent:   { freq1: 880, freq2: 0,   pattern: 'rapid' },
        subtle:   { freq1: 392, freq2: 0,   pattern: 'slow' },
      };
      const tone = tones[toneId] || tones.classic;
      
      const playBurst = () => {
        if (!audioContextRef.current || !(audioContextRef.current instanceof AudioContext)) return;
        const t = ctx.currentTime;
        
        const playNote = (startTime, freq, dur) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = freq;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0, startTime);
          gain.gain.linearRampToValueAtTime(0.3, startTime + 0.03);
          gain.gain.linearRampToValueAtTime(0.3, startTime + dur - 0.05);
          gain.gain.linearRampToValueAtTime(0, startTime + dur);
          osc.start(startTime);
          osc.stop(startTime + dur);
        };
        
        if (tone.pattern === 'double') {
          playNote(t, tone.freq1, 0.4);
          if (tone.freq2) playNote(t, tone.freq2, 0.4);
          playNote(t + 0.5, tone.freq1, 0.4);
          if (tone.freq2) playNote(t + 0.5, tone.freq2, 0.4);
        } else if (tone.pattern === 'single') {
          playNote(t, tone.freq1, 0.6);
          if (tone.freq2) playNote(t + 0.2, tone.freq2, 0.4);
        } else if (tone.pattern === 'triple') {
          playNote(t, tone.freq1, 0.2);
          playNote(t + 0.25, tone.freq2 || tone.freq1, 0.2);
          playNote(t + 0.5, tone.freq1, 0.3);
        } else if (tone.pattern === 'rapid') {
          for (let i = 0; i < 4; i++) {
            playNote(t + i * 0.15, tone.freq1, 0.1);
          }
        } else if (tone.pattern === 'slow') {
          playNote(t, tone.freq1, 1.0);
        }
      };
      
      playBurst();
      
      ringtoneIntervalRef.current = setInterval(() => {
        if (audioContextRef.current && audioContextRef.current instanceof AudioContext) playBurst();
      }, 3000);
      
    } catch (e) {
      console.warn("Could not play ringtone:", e);
    }
  }, [stopRingtone]);

  // ==================== AUDIO SETUP ====================

  const setupRemoteAudio = useCallback(async (rtcSession) => {
    console.log("Setting up remote audio...");
    
    const pc = rtcSession.connection;
    if (!pc) {
      console.error("No peer connection found!");
      return;
    }
    
    console.log("PeerConnection state:", pc.connectionState);
    console.log("ICE connection state:", pc.iceConnectionState);
    
    const remoteStream = new MediaStream();
    const receivers = pc.getReceivers();
    console.log("Found receivers:", receivers.length);
    
    receivers.forEach((receiver, idx) => {
      if (receiver.track) {
        console.log(`Receiver ${idx}: kind=${receiver.track.kind}, enabled=${receiver.track.enabled}`);
        remoteStream.addTrack(receiver.track);
      }
    });

    console.log("Remote stream tracks:", remoteStream.getTracks().length);
    remoteStreamRef.current = remoteStream;

    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    
    audioRef.current.srcObject = remoteStream;
    
    const preferredSpeaker = localStorage.getItem('preferredSpeaker');
    if (preferredSpeaker && typeof audioRef.current.setSinkId === 'function') {
      try {
        await audioRef.current.setSinkId(preferredSpeaker);
        console.log("Audio output set to:", preferredSpeaker);
        setOutputDeviceId(preferredSpeaker);
      } catch (error) {
        console.warn("Could not set audio output device:", error);
        // Device ID might have changed - try to find by label
        const savedLabel = localStorage.getItem('preferredSpeakerLabel');
        if (savedLabel) {
          try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const matchingDevice = devices.find(d => d.kind === 'audiooutput' && d.label === savedLabel);
            if (matchingDevice) {
              await audioRef.current.setSinkId(matchingDevice.deviceId);
              console.log("Audio output set by label match:", savedLabel, "->", matchingDevice.deviceId);
              localStorage.setItem('preferredSpeaker', matchingDevice.deviceId);
              setOutputDeviceId(matchingDevice.deviceId);
            }
          } catch (labelError) {
            console.warn("Could not find device by label:", labelError);
          }
        }
      }
    }

    try {
      await audioRef.current.play();
      console.log("Audio playback started successfully");
    } catch (error) {
      console.error("Error playing audio:", error);
    }
    
    pc.oniceconnectionstatechange = () => {
      console.log("ICE connection state changed:", pc.iceConnectionState);
    };
    
    pc.onconnectionstatechange = () => {
      console.log("Connection state changed:", pc.connectionState);
    };
  }, []);

  // ==================== JSSIP SESSION HANDLERS ====================

  const setupJsSipSessionHandlers = useCallback((rtcSession, isIncoming = false) => {
    sessionRef.current = rtcSession;
    let isTerminated = false;

    // Handle session progress (ringing)
    rtcSession.on('progress', (e) => {
      console.log("Call progress:", e.originator);
      if (!isIncoming) {
        setCallStatus("ringing");
        reportStatus("ringing");
      }
    });

    // Handle session confirmation (call established)
    rtcSession.on('confirmed', () => {
      console.log("Call confirmed/established");
      setCallStatus("connected");
      
      // Report busy status with the remote party number
      const remoteUri = rtcSession.remote_identity?.uri?.user || '';
      reportStatus("busy", remoteUri);
      stopRingtone();
      
      // Mark that this call was actually answered by this user
      if (currentCallInfoRef.current) {
        currentCallInfoRef.current.wasAnswered = true;
      }
      
      // Start call timer
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
      
      // Start heartbeat to keep softphone status fresh during long calls
      if (statusHeartbeatRef.current) clearInterval(statusHeartbeatRef.current);
      statusHeartbeatRef.current = setInterval(() => {
        reportStatus("busy", remoteUri);
      }, 60000); // Re-report every 60 seconds
      
      // Setup remote audio
      setupRemoteAudio(rtcSession);
    });

    // Handle call end
    rtcSession.on('ended', (e) => {
      if (isTerminated) return;
      isTerminated = true;
      
      console.log("Call ended:", e.cause);
      if (statusHeartbeatRef.current) {
        clearInterval(statusHeartbeatRef.current);
        statusHeartbeatRef.current = null;
      }
      cleanupAfterCall();
    });

    // Handle call failure
    rtcSession.on('failed', (e) => {
      if (isTerminated) return;
      isTerminated = true;
      
      const sipCode = e.message?.status_code;
      const sipReason = e.message?.reason_phrase;
      console.log("Call failed:", e.cause, "originator:", e.originator, "SIP response:", sipCode, sipReason);
      
      // Show appropriate error message
      if (e.cause !== 'Canceled' && e.cause !== 'Rejected') {
        if (e.cause === 'Authentication Error') {
          toast.error(
            `Call rejected by PBX${sipCode ? ` (${sipCode} ${sipReason || ''})` : ''}. Your SIP password may be out of date — log out and sign back in using your current PBX SIP password.`,
            { duration: 10000 }
          );
        } else {
          toast.error(`Call failed: ${e.cause || 'Unknown error'}${sipCode ? ` (${sipCode} ${sipReason || ''})` : ''}`);
        }
      }
      
      if (statusHeartbeatRef.current) {
        clearInterval(statusHeartbeatRef.current);
        statusHeartbeatRef.current = null;
      }
      cleanupAfterCall();
    });

    // Handle DTMF
    rtcSession.on('newDTMF', (e) => {
      console.log("DTMF received:", e.dtmf.tone);
    });

    // Handle hold/unhold
    rtcSession.on('hold', (e) => {
      console.log("Call on hold by:", e.originator);
      if (e.originator === 'local') {
        setIsOnHold(true);
        toast.info("Call on hold");
      }
    });

    rtcSession.on('unhold', (e) => {
      console.log("Call resumed by:", e.originator);
      if (e.originator === 'local') {
        setIsOnHold(false);
        toast.info("Call resumed");
      }
    });

    // Handle refer (transfer)
    rtcSession.on('refer', (e) => {
      console.log("Transfer request received");
    });

    // Handle ICE candidates
    rtcSession.on('icecandidate', (e) => {
      console.log("ICE candidate:", e.candidate?.candidate);
    });

    // Handle peerconnection events
    rtcSession.on('peerconnection', (e) => {
      console.log("PeerConnection created");
      const pc = e.peerconnection;
      
      pc.oniceconnectionstatechange = () => {
        console.log("ICE state:", pc.iceConnectionState);
      };
      
      pc.onconnectionstatechange = () => {
        console.log("Connection state:", pc.connectionState);
      };
      
      pc.ontrack = (event) => {
        console.log("Remote track received:", event.track.kind);
      };
    });

    // Fix SDP compatibility: strip SDES crypto lines from remote offers
    // Chrome/Electron WebRTC rejects SDP with both SDES and DTLS-SRTP
    rtcSession.on('sdp', (e) => {
      console.log("SDP event - type:", e.type, "originator:", e.originator);
      if (e.originator === 'remote') {
        const hasCrypto = e.sdp.includes('a=crypto:');
        const hasFingerprint = e.sdp.includes('a=fingerprint:');
        if (hasCrypto && hasFingerprint) {
          console.log("[SDP Fix] Stripping SDES crypto lines (DTLS-SRTP will be used)");
          e.sdp = e.sdp.split('\r\n')
            .filter(line => !line.startsWith('a=crypto:'))
            .join('\r\n');
        }
      }
    });

    // Log getUserMedia failures
    rtcSession.on('getusermediafailed', (e) => {
      console.error("JsSIP getUserMedia failed:", e);
    });
  }, [reportStatus, stopRingtone, setupRemoteAudio]);

  // Cleanup after call ends
  const cleanupAfterCall = useCallback(() => {
    // Check if we should prompt to add contact (unknown caller/callee)
    // Only prompt if: the call was actually answered AND the number is unknown
    const callInfo = currentCallInfoRef.current;
    if (callInfo && callInfo.wasAnswered && !callInfo.isKnownContact && callInfo.remoteNumber) {
      // Only prompt for external numbers (not internal extensions)
      const isExternal = callInfo.remoteNumber.length > 6;
      if (isExternal) {
        setEndedCallInfo({
          number: callInfo.remoteNumber,
          name: callInfo.remoteName,
          direction: callInfo.direction,
          duration: callDuration
        });
      }
    }
    currentCallInfoRef.current = null;
    
    setCallStatus("idle");
    setIncomingCallInfo(null);
    setCallerDetails(null);
    setCallerHistory([]);
    setCallDuration(0);
    setIsMuted(false);
    setIsOnHold(false);
    reportStatus("available");
    stopRingtone();
    
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    
    // Release local media stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    sessionRef.current = null;
  }, [reportStatus, stopRingtone, callDuration]);

  // ==================== SIP CONNECTION (JSSIP) ====================

  const initializeSip = useCallback(async () => {
    // Get SIP password - prefer user object, fall back to locally saved credentials
    let sipPassword = user?.sip_password;
    if (!sipPassword && user?.phone_number) {
      sipPassword = getSavedSipCredentials(user.phone_number);
      if (sipPassword) {
        console.log("Using locally saved SIP credentials");
      }
    }
    
    if (!user?.phone_number || !sipPassword || !user?.sip_domain) {
      console.log("Missing SIP credentials");
      return;
    }

    // Skip SIP registration if dialer is disabled (DND mode)
    if (user?.dialer_enabled === false) {
      console.log("Dialer disabled — skipping SIP registration");
      setSipStatus("disconnected");
      sipStatusRef.current = "disconnected";
      return;
    }

    if (sipStatusRef.current === "registered" || sipStatusRef.current === "connecting") {
      console.log("Already connected or connecting");
      return;
    }

    setSipStatus("connecting");
    sipStatusRef.current = "connecting";

    // Auto-detect provider from API response
    const provider = detectSipProvider(user.sip_domain);
    if (!provider) {
      console.error("Could not detect SIP provider from:", user.sip_domain);
      setSipStatus("failed");
      sipStatusRef.current = "failed";
      toast.error("Unknown SIP provider");
      return;
    }
    
    setSipProvider(provider);
    console.log("Detected SIP provider:", provider.name);
    console.log("WebSocket URL:", provider.wsServer);
    console.log("SIP Domain:", provider.sipDomain);

    try {
      // Create WebSocket connections - primary first, then fallbacks (JsSIP fails over by weight)
      const sockets = [{ socket: new JsSIP.WebSocketInterface(provider.wsServer), weight: 20 }];
      for (const fallbackUrl of provider.fallbackWsServers || []) {
        if (fallbackUrl !== provider.wsServer) {
          try {
            sockets.push({ socket: new JsSIP.WebSocketInterface(fallbackUrl), weight: 10 });
            console.log("Fallback WSS server configured:", fallbackUrl);
          } catch (e) {
            console.warn("Invalid fallback WSS URL:", fallbackUrl);
          }
        }
      }
      
      // JsSIP configuration
      const configuration = {
        sockets,
        uri: `sip:${user.phone_number}@${provider.sipDomain}`,
        password: sipPassword,  // Use resolved password (from user or local storage)
        display_name: user.name || user.phone_number,
        register: true,
        register_expires: 300,
        session_timers: true,
        session_timers_refresh_method: 'update',
        use_preloaded_route: false,
        // Disable JsSIP's built-in logging for cleaner console
        // Enable for debugging: JsSIP.debug.enable('JsSIP:*');
      };

      // Create UserAgent
      const ua = new JsSIP.UA(configuration);
      userAgentRef.current = ua;

      // Connection timeout
      const connectionTimeout = setTimeout(() => {
        if (sipStatusRef.current === "connecting") {
          setSipStatus("failed");
          sipStatusRef.current = "failed";
          toast.error("Connection timed out");
          ua.stop();
        }
      }, 25000);

      // Event handlers
      ua.on('connecting', () => {
        console.log("JsSIP: Connecting to", provider.wsServer);
      });

      ua.on('connected', (e) => {
        console.log("JsSIP: WebSocket connected via", e?.socket?.url || provider.wsServer);
      });

      ua.on('disconnected', () => {
        console.log("JsSIP: WebSocket disconnected");
        clearTimeout(connectionTimeout);
        if (sipStatusRef.current === "registered") {
          setSipStatus("failed");
          sipStatusRef.current = "failed";
          toast.error("Connection lost");
        }
      });

      ua.on('registered', () => {
        console.log("JsSIP: Registered successfully");
        clearTimeout(connectionTimeout);
        setSipStatus("registered");
        sipStatusRef.current = "registered";
        reportStatus("available");
        toast.success("Phone connected", { id: 'sip-status' });
      });

      ua.on('unregistered', () => {
        console.log("JsSIP: Unregistered");
        setSipStatus("disconnected");
        sipStatusRef.current = "disconnected";
      });

      ua.on('registrationFailed', (e) => {
        console.error("JsSIP: Registration failed:", e.cause);
        clearTimeout(connectionTimeout);
        setSipStatus("failed");
        sipStatusRef.current = "failed";
        toast.error(`Registration failed: ${e.cause || 'Unknown error'}`);
      });

      // Handle incoming calls
      ua.on('newRTCSession', (e) => {
        const rtcSession = e.session;
        
        if (e.originator === 'remote') {
          // Incoming call
          console.log("Incoming call from:", rtcSession.remote_identity.uri.user);
          
          const callerNumber = rtcSession.remote_identity.uri.user;
          const callerName = rtcSession.remote_identity.display_name || callerNumber;
          
          // Track call info for "add contact" prompt
          currentCallInfoRef.current = {
            remoteNumber: callerNumber,
            remoteName: callerName !== callerNumber ? callerName : null,
            direction: 'incoming',
            isKnownContact: false, // Will be updated by fetchCallerInfo
            wasAnswered: false // Will be set to true when THIS user answers
          };
          
          setIncomingCallInfo({
            number: callerNumber,
            name: callerName
          });
          
          setCallStatus("incoming");
          fetchCallerInfo(callerNumber);
          
          sessionRef.current = rtcSession;
          playRingtone();
          
          toast.info(`Incoming call from ${callerName}`, {
            duration: 30000,
            id: 'incoming-call'
          });
          
          // If running in Electron desktop app, auto-show the window
          if (window.electronAPI?.isElectron) {
            console.log('[CONNECT Desktop] Auto-showing window for incoming call');
            window.electronAPI.showWindow?.();
          }
          
          // Show browser notification (works even when tab is in background)
          notificationService.showIncomingCallNotification(
            callerName,
            callerNumber,
            () => {
              // When notification is clicked, focus window - call is already ringing
              window.focus();
            }
          );
          
          // Setup session handlers
          setupJsSipSessionHandlers(rtcSession, true);
        }
      });

      // Start the UserAgent
      ua.start();

    } catch (error) {
      console.error("SIP connection error:", error);
      setSipStatus("failed");
      sipStatusRef.current = "failed";
      toast.error(`Connection failed: ${error.message}`);
    }
  }, [user, fetchCallerInfo, reportStatus, playRingtone, setupJsSipSessionHandlers]);

  // Disconnect SIP
  const disconnectSip = useCallback(async () => {
    if (userAgentRef.current) {
      try {
        userAgentRef.current.stop();
      } catch (e) {
        console.warn("Stop error:", e);
      }
    }
    userAgentRef.current = null;
    setSipStatus("disconnected");
    sipStatusRef.current = "disconnected";
    setSipProvider(null);
  }, []);

  // ==================== CALL ACTIONS ====================

  // Answer call
  const answerCall = useCallback(async () => {
    if (!sessionRef.current) {
      console.warn("No session to answer");
      return;
    }
    
    stopRingtone();
    toast.dismiss('incoming-call');
    notificationService.closeNotification('incoming-call');
    
    try {
      // Pre-acquire media stream so we get the real error if it fails
      const preferredMic = localStorage.getItem('preferredMicrophone');
      const audioConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };
      
      let localStream = null;
      
      if (preferredMic) {
        // Try preferred mic first, fall back to any mic
        try {
          localStream = await navigator.mediaDevices.getUserMedia({
            audio: { ...audioConstraints, deviceId: { exact: preferredMic } },
            video: false
          });
          console.log("Got media stream with preferred mic");
        } catch (micError) {
          console.warn("Preferred mic failed, trying default:", micError.name, micError.message);
          localStream = await navigator.mediaDevices.getUserMedia({
            audio: audioConstraints,
            video: false
          });
          console.log("Got media stream with default mic");
        }
      } else {
        localStream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
          video: false
        });
        console.log("Got media stream with default mic");
      }

      const options = {
        mediaStream: localStream,
        pcConfig: {
          iceServers: getIceServers()
        },
        rtcAnswerConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false
        },
        sessionTimersExpires: 120
      };

      sessionRef.current.answer(options);
      console.log("Call answered");
      
    } catch (error) {
      console.error("Error answering call:", error.name, error.message);
      toast.error(`Failed to answer: ${error.message || 'Microphone access denied'}`);
      cleanupAfterCall();
    }
  }, [stopRingtone, getIceServers, cleanupAfterCall]);

  // Reject call
  const rejectCall = useCallback(() => {
    if (!sessionRef.current) return;
    
    stopRingtone();
    toast.dismiss('incoming-call');
    notificationService.closeNotification('incoming-call');
    
    try {
      sessionRef.current.terminate({
        status_code: 486, // Busy Here
        reason_phrase: 'Rejected'
      });
    } catch (error) {
      console.error("Error rejecting call:", error);
    }
    
    cleanupAfterCall();
  }, [stopRingtone, cleanupAfterCall]);

  // Hang up
  const hangUp = useCallback(() => {
    if (!sessionRef.current) return;
    
    stopRingtone();
    
    try {
      sessionRef.current.terminate();
    } catch (error) {
      console.error("Error ending call:", error);
    }
  }, [stopRingtone]);

  // Make call
  const makeCall = useCallback(async (phoneNumber, contactInfo = null) => {
    if (!userAgentRef.current || sipStatus !== "registered") {
      toast.error("Phone not connected");
      return false;
    }

    if (callStatus !== "idle") {
      toast.error("Already in a call");
      return false;
    }

    const sipDomain = getSipDomain();
    if (!sipDomain) {
      toast.error("SIP domain not configured");
      return false;
    }

    const cleanNumber = phoneNumber.replace(/\s/g, "");
    const targetUri = `sip:${cleanNumber}@${sipDomain}`;
    
    console.log("Making call to:", targetUri);
    
    // Track call info for "add contact" prompt
    currentCallInfoRef.current = {
      remoteNumber: cleanNumber,
      remoteName: contactInfo?.name || null,
      direction: 'outgoing',
      isKnownContact: !!contactInfo?.name,
      wasAnswered: false // Will be set to true when call is confirmed/accepted
    };

    try {
      const preferredMic = localStorage.getItem('preferredMicrophone');
      const audioConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };
      
      if (preferredMic) {
        audioConstraints.deviceId = { exact: preferredMic };
      }

      const options = {
        mediaConstraints: {
          audio: audioConstraints,
          video: false
        },
        pcConfig: {
          iceServers: getIceServers()
        },
        rtcOfferConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false
        },
        sessionTimersExpires: 120
      };

      const rtcSession = userAgentRef.current.call(targetUri, options);
      
      if (!rtcSession) {
        toast.error("Failed to initiate call");
        currentCallInfoRef.current = null;
        return false;
      }
      
      setupJsSipSessionHandlers(rtcSession, false);
      
      setIncomingCallInfo({
        number: phoneNumber,
        name: contactInfo?.name || phoneNumber
      });
      
      setCallStatus("ringing");
      
      return true;
    } catch (error) {
      console.error("Error making call:", error);
      toast.error("Failed to make call");
      currentCallInfoRef.current = null;
      return false;
    }
  }, [sipStatus, callStatus, getSipDomain, getIceServers, setupJsSipSessionHandlers]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (!sessionRef.current) return;
    
    const newMutedState = !isMuted;
    
    if (newMutedState) {
      sessionRef.current.mute({ audio: true });
    } else {
      sessionRef.current.unmute({ audio: true });
    }
    
    setIsMuted(newMutedState);
    console.log("Mute toggled:", newMutedState ? "MUTED" : "UNMUTED");
  }, [isMuted]);

  // Toggle speaker
  const toggleSpeaker = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.muted = isSpeakerOn;
      setIsSpeakerOn(!isSpeakerOn);
    }
  }, [isSpeakerOn]);

  // Toggle hold
  const toggleHold = useCallback(async () => {
    if (!sessionRef.current || callStatus !== "connected") return;
    
    try {
      if (isOnHold) {
        sessionRef.current.unhold();
      } else {
        sessionRef.current.hold();
      }
      // State update handled by hold/unhold event handlers
    } catch (error) {
      console.error("Hold toggle failed:", error);
      toast.error("Failed to toggle hold");
    }
  }, [isOnHold, callStatus]);

  // Send DTMF
  const sendDtmf = useCallback((digit) => {
    if (sessionRef.current && callStatus === "connected") {
      try {
        sessionRef.current.sendDTMF(digit);
        console.log("DTMF sent:", digit);
      } catch (error) {
        console.warn("DTMF failed:", error);
      }
    }
  }, [callStatus]);

  // Blind transfer
  const blindTransfer = useCallback((targetNumber) => {
    if (!sessionRef.current || callStatus !== "connected") {
      toast.error("No active call to transfer");
      return false;
    }
    
    const sipDomain = getSipDomain();
    if (!sipDomain) return false;
    
    try {
      const targetUri = `sip:${targetNumber}@${sipDomain}`;
      
      // JsSIP refer() returns an RTCSession.ReferSubscriber with events
      const referSubscriber = sessionRef.current.refer(targetUri, {
        eventHandlers: {
          requestSucceeded: (e) => {
            console.log("REFER request succeeded (202 Accepted)");
          },
          requestFailed: (e) => {
            console.error("REFER request failed:", e.cause);
            toast.error("Transfer failed: " + (e.cause || "Request rejected"));
          },
          accepted: (e) => {
            console.log("REFER accepted, transfer in progress");
            toast.success("Transfer accepted");
          },
          failed: (e) => {
            console.error("REFER failed:", e.cause);
            toast.error("Transfer failed: " + (e.cause || "Unknown error"));
          }
        }
      });
      
      // Listen for NOTIFY events to know when transfer completes
      if (referSubscriber) {
        referSubscriber.on('notify', (notification) => {
          console.log("REFER NOTIFY received:", notification.body);
          
          // Parse the SIP status from the NOTIFY body
          // Format is typically "SIP/2.0 200 OK" or "SIP/2.0 180 Ringing"
          const match = notification.body?.match(/SIP\/2\.0\s+(\d+)/);
          if (match) {
            const statusCode = parseInt(match[1]);
            console.log("Transfer status code:", statusCode);
            
            // 200 = Transfer successful, hang up original call
            if (statusCode === 200) {
              console.log("Transfer successful (200 OK), hanging up original call");
              toast.success("Transfer complete");
              // Small delay to ensure NOTIFY is acknowledged before hangup
              setTimeout(() => {
                if (sessionRef.current) {
                  sessionRef.current.terminate();
                }
              }, 200);
            } 
            // 1xx codes are provisional (ringing, trying) - keep waiting
            else if (statusCode >= 100 && statusCode < 200) {
              console.log("Transfer in progress...");
            }
            // 4xx, 5xx, 6xx are errors
            else if (statusCode >= 400) {
              console.error("Transfer failed with status:", statusCode);
              toast.error("Transfer failed");
            }
          }
        });
      }
      
      toast.info(`Transferring to ${targetNumber}...`);
      return true;
    } catch (error) {
      console.error("Blind transfer failed:", error);
      toast.error("Transfer failed");
      return false;
    }
  }, [callStatus, getSipDomain]);

  // ==================== EFFECTS ====================

  // Initialize notification service when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      notificationService.init(token).then(success => {
        if (success) {
          console.log('[SIP] Notification service initialized');
        } else {
          console.log('[SIP] Notification service not available');
        }
      });
    }
  }, [isAuthenticated, token]);

  // Helper to check if SIP credentials are available (from user or local storage)
  const hasSipCredentials = useCallback(() => {
    if (user?.sip_password) return true;
    if (user?.phone_number) {
      const savedPassword = getSavedSipCredentials(user.phone_number);
      return !!savedPassword;
    }
    return false;
  }, [user?.sip_password, user?.phone_number]);

  // Handle tab visibility changes - reconnect SIP if connection was lost while tab was hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[SIP] Tab became visible, checking connection status...');
        
        // If we should be connected but aren't, reconnect
        if (isAuthenticated && isInAppArea && user?.phone_number && hasSipCredentials() && user?.sip_domain) {
          // Check if UserAgent is still connected
          const ua = userAgentRef.current;
          if (!ua || !ua.isConnected || !ua.isConnected()) {
            console.log('[SIP] Connection lost while tab was hidden, reconnecting...');
            setSipStatus("disconnected");
            sipStatusRef.current = "disconnected";
            // Small delay before reconnecting to let browser fully resume
            setTimeout(() => {
              initializeSip();
            }, 500);
          } else {
            console.log('[SIP] Connection still active');
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, isInAppArea, user?.phone_number, hasSipCredentials, user?.sip_domain, initializeSip]);

  // Auto-connect when authenticated and in app area
  useEffect(() => {
    const hasActiveCall = callStatus === "connected" || callStatus === "ringing" || sessionRef.current;
    const dialerOn = user?.dialer_enabled !== false;
    
    if (isAuthenticated && isInAppArea && user?.phone_number && hasSipCredentials() && user?.sip_domain && dialerOn) {
      if (sipStatus === "disconnected") {
        initializeSip();
      }
    } else if ((!isInAppArea || !dialerOn) && sipStatus !== "disconnected" && !hasActiveCall) {
      disconnectSip();
    }
    
    return () => {
      const activeCall = callStatus === "connected" || callStatus === "ringing" || sessionRef.current;
      if (!activeCall) {
        disconnectSip();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isInAppArea, user?.phone_number, hasSipCredentials, user?.sip_domain]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRingtone();
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      if (audioRef.current) {
        audioRef.current.srcObject = null;
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [stopRingtone]);
  
  // Dismiss the "add contact" prompt
  const dismissEndedCallPrompt = useCallback(() => {
    setEndedCallInfo(null);
  }, []);

  // ==================== ELECTRON DESKTOP APP INTEGRATION ====================
  // Expose a global function for the Electron shell to call when notification is clicked
  useEffect(() => {
    // Function to handle incoming call notification click from Electron
    window.focusIncomingCall = () => {
      console.log('[CONNECT Desktop] focusIncomingCall triggered');
      
      // Focus the window
      window.focus();
      
      // If there's an incoming call, we could auto-answer or just focus
      // For now, just ensure the window is visible - user can click answer
      if (callStatus === 'incoming' && sessionRef.current) {
        console.log('[CONNECT Desktop] Incoming call detected, focusing app');
        // Dispatch a custom event that components can listen to
        window.dispatchEvent(new CustomEvent('electron-focus-incoming-call', {
          detail: { callInfo: incomingCallInfo }
        }));
      }
    };

    // Also expose answerCall for potential direct answer from notification
    window.answerIncomingCall = () => {
      console.log('[CONNECT Desktop] answerIncomingCall triggered');
      if (callStatus === 'incoming' && sessionRef.current) {
        answerCall();
      }
    };

    // Listen for auto-answer event from service worker (browser notification "Answer" button)
    const handleAutoAnswer = () => {
      console.log('[SIP] auto-answer-call event received');
      if (callStatus === 'incoming' && sessionRef.current) {
        console.log('[SIP] Auto-answering incoming call');
        answerCall();
      } else {
        console.log('[SIP] No incoming call to answer, callStatus:', callStatus);
      }
    };
    window.addEventListener('auto-answer-call', handleAutoAnswer);

    // Cleanup
    return () => {
      delete window.focusIncomingCall;
      delete window.answerIncomingCall;
      window.removeEventListener('auto-answer-call', handleAutoAnswer);
    };
  }, [callStatus, incomingCallInfo, answerCall]);

  const value = {
    // State
    sipStatus,
    callStatus,
    incomingCallInfo,
    callerDetails,
    callerHistory,
    callDuration,
    isMuted,
    isSpeakerOn,
    isOnHold,
    outputDeviceId,
    sipProvider,
    endedCallInfo,
    
    // Actions
    initializeSip,
    disconnectSip,
    makeCall,
    answerCall,
    rejectCall,
    hangUp,
    toggleMute,
    toggleSpeaker,
    toggleHold,
    sendDtmf,
    fetchCallerInfo,
    blindTransfer,
    dismissEndedCallPrompt,
    
    // Refs for advanced usage
    userAgentRef,
    sessionRef,
    
    // Helpers
    getSipDomain,
    getIceServers,
    setupJsSipSessionHandlers
  };

  return (
    <SipContext.Provider value={value}>
      {children}
    </SipContext.Provider>
  );
}
